import { useEffect, useRef } from "react";

export type SaveStatus = "idle" | "saving" | "saved" | "error";

export interface UseDebouncedSaveOptions<T> {
  /** The value to track — effect re-runs when it changes. */
  value: T;
  /** The async save function called after the debounce. */
  save: (value: T) => Promise<void> | void;
  /** Debounce delay in ms. Default: 500. */
  delayMs?: number;
  /**
   * Optional predicate — if provided and returns false, the save is skipped
   * (useful for content-based fingerprinting to avoid saving when nothing
   * meaningful changed).
   */
  shouldSave?: (value: T) => boolean;
  /** Status transitions callback. */
  onStatus?: (status: SaveStatus) => void;
  /**
   * When true, the hook is disabled (no timers set, no saves fire). Useful
   * when a dependency like a drawing ID isn't yet available.
   */
  enabled?: boolean;
}

export interface UseDebouncedSaveApi {
  /** Cancel any pending save and trigger the save synchronously. */
  flush: () => void;
}

/**
 * Generic debounced save hook with status transitions and optional
 * fingerprint-based skip.
 */
export const useDebouncedSave = <T>({
  value,
  save,
  delayMs = 500,
  shouldSave,
  onStatus,
  enabled = true,
}: UseDebouncedSaveOptions<T>): UseDebouncedSaveApi => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const saveRef = useRef(save);
  saveRef.current = save;
  const shouldSaveRef = useRef(shouldSave);
  shouldSaveRef.current = shouldSave;
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;

  const runSave = async () => {
    const v = valueRef.current;
    if (shouldSaveRef.current && !shouldSaveRef.current(v)) {
      return;
    }
    onStatusRef.current?.("saving");
    try {
      await saveRef.current(v);
      onStatusRef.current?.("saved");
    } catch {
      onStatusRef.current?.("error");
    }
  };

  useEffect(() => {
    if (!enabled) {
      return;
    }
    onStatusRef.current?.("idle");
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(runSave, delayMs);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, enabled, delayMs]);

  return {
    flush: () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      runSave();
    },
  };
};
